import React, { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
  clearMoodError,
  fetchAllMoodEntries,
  selectAllMoodEntries,
  selectAllMoodStatus,
  selectMoodError,
} from '../../redux/mood/moodSlice'
import { todayKey } from '../../redux/tasks'
import type { MoodEntryDto } from '../../Models/Mood'
import MoodSection from '../../Components/MoodSection/MoodSection'
import { MoodEntryCard } from '../../Components/MoodEntryCard'
import styles from './Mood.module.css'

const formatDayHeading = (dateKey: string): string => {
  const d = new Date(`${dateKey}T12:00:00`)
  if (Number.isNaN(d.getTime())) return dateKey
  return d.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const MoodScreen = () => {
  const dispatch = useAppDispatch()
  const allEntries = useAppSelector(selectAllMoodEntries)
  const allStatus = useAppSelector(selectAllMoodStatus)
  const error = useAppSelector(selectMoodError)
  const today = todayKey()

  useEffect(() => {
    void dispatch(fetchAllMoodEntries())
  }, [dispatch])

  const grouped = useMemo(() => {
    const map = new Map<string, MoodEntryDto[]>()
    for (const e of allEntries) {
      const dk = e.loggedDate
      if (!map.has(dk)) map.set(dk, [])
      map.get(dk)!.push(e)
    }
    const rows = Array.from(map.entries()).map(([dateKey, list]) => ({
      dateKey,
      entries: [...list].sort(
        (a, b) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime()
      ),
    }))
    rows.sort((a, b) => b.dateKey.localeCompare(a.dateKey))
    return rows
  }, [allEntries])

  return (
    <div className={styles.container}>
      <header className={styles.hero}>
        <span className={styles.kicker}>Capture your feelings</span>
        <h1 className={styles.title}>How are you feeling?</h1>
      </header>

      {error ? (
        <p className={styles.hint} role="alert">
          {error}{' '}
          <button type="button" className={styles.retryBtn} onClick={() => dispatch(clearMoodError())}>
            Dismiss
          </button>
        </p>
      ) : null}

      <section className={styles.section} aria-labelledby="mood-log-heading">
        <h2 id="mood-log-heading" className={styles.sectionTitle}>
          Log a mood
        </h2>
        <MoodSection dateKey={today} variant="formOnly" />
      </section>

      <section className={styles.section} aria-labelledby="mood-history-heading">
        <h2 id="mood-history-heading" className={styles.sectionTitle}>
          History
        </h2>
        <div className={styles.historyPanel}>
          {allStatus === 'loading' ? (
            <div className={styles.skeleton} aria-hidden>
              <div className={styles.skeletonLineMed} />
              <div className={styles.skeletonLineShort} />
              <div className={styles.skeletonLine} />
            </div>
          ) : null}

          {allStatus === 'failed' ? (
            <p className={styles.emptyHistory}>
              Could not load history.{' '}
              <button
                type="button"
                className={styles.retryBtn}
                onClick={() => void dispatch(fetchAllMoodEntries())}
              >
                Try again
              </button>
            </p>
          ) : null}

          {allStatus === 'succeeded' && grouped.length === 0 ? (
            <p className={styles.emptyHistory}>No mood entries yet. Add one above.</p>
          ) : null}

          {allStatus === 'succeeded' && grouped.length > 0
            ? grouped.map(({ dateKey, entries }) => (
                <div key={dateKey} className={styles.dayGroup}>
                  <h3 className={styles.dayHeading}>
                    {dateKey === today ? 'Today' : formatDayHeading(dateKey)}
                  </h3>
                  <div className={styles.dayEntries}>
                    {entries.map((e) => (
                      <MoodEntryCard key={e.id} entry={e} dateKey={e.loggedDate} />
                    ))}
                  </div>
                </div>
              ))
            : null}
        </div>
      </section>
    </div>
  )
}

export default MoodScreen
